import { findIndex } from 'lodash';
import { kwiffCookieAcceptanceKey } from '../../data-types/cookies/cookiesConstants';
import { hasAcceptedCookies } from '../../utils/cookies';
import {
  DECREASE_LIMIT_CONFIRMATION_DIALOG_COUNT,
  DEQUEUE_ALL_SYSTEM_BET_STAKE_UPDATE_DATA,
  DEQUEUE_DIALOG,
  DEQUEUE_NOTIFICATION,
  DEQUEUE_SYSTEM_BET_STAKE_UPDATE_DATA,
  DialogActionType,
  DialogState,
  ENQUEUE_DIALOG,
  ENQUEUE_NOTIFICATION,
  INCREASE_LIMIT_CONFIRMATION_DIALOG_COUNT,
  SET_CASINO_GAME_TAG_DIALOG_DATA,
  SET_CHAT_COOKIE_DIALOG,
  SET_CONFIRMATION_DIALOG_DATA,
  SET_COOKIE_SNACKBAR,
  SET_DISPLAY_APP_INSTALL_PROMPT,
  SET_DOCUMENT_UPLOAD_DATA,
  SET_ERROR_DIALOG,
  SET_INFORMATIVE_DIALOG_DATA,
  SET_IS_INBOX_OPEN,
  SET_MY_PROMOTIONS_DIALOG_OPEN,
  SET_NET_PROMOTER_SCORE_DIALOG,
  SET_NON_UK_USER_VERIFICATION_DIALOG,
  SET_POST_HOG_SURVEY_DIALOG,
  SET_REALITY_CHECK_DIALOG,
  SET_REVIEW_DIALOG_OPEN,
  SET_SYSTEM_BET_STAKE_UPDATE_DIALOG_DATA,
  SET_SYSTEM_BETS_OVERVIEW_DIALOG_DATA,
  SET_USER_CONFIRM_PRODUCT_UNBLOCK_DIALOG,
  TOGGLE_GHOST_USER_DIALOG,
} from '../types/dialogTypes';
import { RESET_STATE } from '../types/stateTypes';

export const initialDialogState: DialogState = {
  queuedDialogs: [],
  queuedNotifications: [],
  generalErrorDialogMessage: '',
  ghostUserDialogData: { open: false },
  cookieSnackbarOpen: localStorage.getItem(kwiffCookieAcceptanceKey) ? false : true,
  chatCookieDialogOpen: false,
  realityCheckDialog: false,
  displayAppInstallPrompt: false,
  userProductUnblockConfirmDialog: false,
  limitConfirmDialogCount: 0,
  nonUkUserVerificationDialogData: {
    open: false,
    messageId: '',
  },
  promptDisplayStatus: {
    reviewOpen: false,
  },
  confirmationDialog: {
    open: false,
  },
  netPromoterScoreDialogOpen: false,
  isInboxOpen: false,
  documentUploadDialogData: {
    open: false,
    proofRequestId: undefined,
    proofRequestName: undefined,
    uploadOption: undefined,
  },
  informativeDialogData: {
    open: false,
    title: '',
    content: '',
  },
  casinoGameTagDialogData: {
    open: false,
    tagId: undefined,
    game: undefined,
  },
  systemBetStakeUpdateQueue: [],
  systemBetSelectionOverviewDialogData: {
    open: false,
    selections: [],
    sportBetTab: undefined,
  },
  postHogSurveyDialogOpen: false,
  myPromotionsDialogOpen: false,
};

export const dialogReducer = (state = initialDialogState, action: DialogActionType): DialogState => {
  switch (action.type) {
    case RESET_STATE:
      return {
        ...initialDialogState,
        cookieSnackbarOpen: hasAcceptedCookies() ? false : true,
      };
    case SET_ERROR_DIALOG:
      return { ...state, generalErrorDialogMessage: action.meta };
    case ENQUEUE_DIALOG:
      return {
        ...state,
        queuedDialogs: [...state.queuedDialogs, action.meta],
      };
    case DEQUEUE_DIALOG:
      return {
        ...state,
        queuedDialogs: state.queuedDialogs.slice(1),
      };
    case ENQUEUE_NOTIFICATION:
      return {
        ...state,
        queuedNotifications: [...state.queuedNotifications, action.meta],
      };
    case DEQUEUE_NOTIFICATION:
      return {
        ...state,
        queuedNotifications: state.queuedNotifications.slice(1),
      };
    case SET_REALITY_CHECK_DIALOG:
      return { ...state, realityCheckDialog: action.meta };
    case TOGGLE_GHOST_USER_DIALOG:
      return { ...state, ghostUserDialogData: action.meta };
    case SET_COOKIE_SNACKBAR: {
      return { ...state, cookieSnackbarOpen: action.meta };
    }
    case SET_CHAT_COOKIE_DIALOG: {
      return { ...state, chatCookieDialogOpen: action.meta };
    }
    case SET_DISPLAY_APP_INSTALL_PROMPT: {
      return { ...state, displayAppInstallPrompt: action.meta };
    }
    case SET_NON_UK_USER_VERIFICATION_DIALOG: {
      return { ...state, nonUkUserVerificationDialogData: action.meta };
    }
    case SET_REVIEW_DIALOG_OPEN: {
      return {
        ...state,
        promptDisplayStatus: {
          ...state.promptDisplayStatus,
          reviewOpen: action.meta,
        },
      };
    }
    case SET_USER_CONFIRM_PRODUCT_UNBLOCK_DIALOG: {
      return { ...state, userProductUnblockConfirmDialog: action.meta };
    }
    case INCREASE_LIMIT_CONFIRMATION_DIALOG_COUNT: {
      return { ...state, limitConfirmDialogCount: state.limitConfirmDialogCount + 1 };
    }
    case DECREASE_LIMIT_CONFIRMATION_DIALOG_COUNT: {
      return { ...state, limitConfirmDialogCount: state.limitConfirmDialogCount - 1 };
    }
    case SET_CONFIRMATION_DIALOG_DATA: {
      return { ...state, confirmationDialog: action.meta };
    }
    case SET_NET_PROMOTER_SCORE_DIALOG: {
      return { ...state, netPromoterScoreDialogOpen: action.meta };
    }
    case SET_IS_INBOX_OPEN: {
      return { ...state, isInboxOpen: action.meta };
    }
    case SET_DOCUMENT_UPLOAD_DATA: {
      return { ...state, documentUploadDialogData: action.meta };
    }
    case SET_INFORMATIVE_DIALOG_DATA: {
      return { ...state, informativeDialogData: action.meta };
    }
    case SET_CASINO_GAME_TAG_DIALOG_DATA: {
      return { ...state, casinoGameTagDialogData: action.meta };
    }
    case SET_SYSTEM_BET_STAKE_UPDATE_DIALOG_DATA: {
      const index = findIndex(state.systemBetStakeUpdateQueue, { title: action.meta.title });
      if (index < 0) {
        return { ...state, systemBetStakeUpdateQueue: [...state.systemBetStakeUpdateQueue, action.meta] };
      }
      return {
        ...state,
        systemBetStakeUpdateQueue: state.systemBetStakeUpdateQueue.map(systemBetStake =>
          systemBetStake.title === action.meta.title ? action.meta : systemBetStake,
        ),
      };
    }
    case SET_SYSTEM_BETS_OVERVIEW_DIALOG_DATA: {
      return { ...state, systemBetSelectionOverviewDialogData: action.meta };
    }
    case DEQUEUE_SYSTEM_BET_STAKE_UPDATE_DATA: {
      return { ...state, systemBetStakeUpdateQueue: state.systemBetStakeUpdateQueue.slice(1) };
    }
    case SET_POST_HOG_SURVEY_DIALOG: {
      return { ...state, postHogSurveyDialogOpen: action.meta };
    }
    case SET_MY_PROMOTIONS_DIALOG_OPEN: {
      return { ...state, myPromotionsDialogOpen: action.meta };
    }
    case DEQUEUE_ALL_SYSTEM_BET_STAKE_UPDATE_DATA:
      return { ...state, systemBetStakeUpdateQueue: [] };
    default:
      return state;
  }
};
